import { FC, useState, useCallback, useEffect } from 'react'
import { FieldValues, useFormContext } from 'react-hook-form'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { SerializedError } from '@reduxjs/toolkit'
import { PlusOutlined } from '@ant-design/icons'
import useFormProcessor from '@hooks/useFormProcessor'
import {
  usePostFinalTestCourseThemeMutation,
  usePutFinalTestCourseThemeMutation,
} from '@services/courseThemes'
import { usePatchCourseFinalTestMutation } from '@services/courses'
import AddNewItemModal, { FromButtons } from '@templates/AddNewItemModal'
import {
  FooterProps,
  prepareValues,
} from '@templates/AddNewItemModalLayout/Footer'
import { numberField } from '@templates/AddNewItemModal/templates'
import {
  parseFormHoursToMinutes,
  themeMinutesToFormHours,
} from '@utils/themeHoursTime'
import MaskedInput from '@organisms/MaskedInput'
import Modal from '@organisms/Modal'
import PrimaryButton from '@organisms/PrimaryButton'
import SecondaryButton from '@organisms/SecondaryButton'
import DeleteButton from '@templates/DeleteButton'
import { CourseThemeDetailed, FinalTestQuestionInput } from '@models/courseTheme'
import ButtonStyle from '@enums/ButtonStyle'
import FinalTestQuestionFormModal from './FinalTestQuestionFormModal'
import {
  InstructionList,
  QuestionsSection,
  QuestionsSectionTitle,
  QuestionsEmptyPlaceholder,
  QuestionsList,
  QuestionRow,
  QuestionText,
  QuestionActions,
  QuestionsErrorText,
  FinalTestFooter,
} from './styled'

const FINAL_TEST_TITLE = 'Итоговое тестирование'

type FormValues = {
  passPercentage?: string
  questionsCount?: string
  hoursTime?: string
}

type Props = {
  courseId: number
  handleCloseModal: () => void
  finalTest?: CourseThemeDetailed | null
  isEdit?: boolean
  onSuccessVisibleChange?: (visible: boolean) => void
}

const fields = [
  {
    fields: [
      {
        name: 'passPercentage',
        label: 'Процент для прохождения*',
        Component: MaskedInput,
        rules: {
          required: { value: true, message: 'Заполните процент' },
          validate: (value: string) => {
            const num = Number(value)
            if (Number.isNaN(num) || num < 0 || num > 100) {
              return 'Процент должен быть от 0 до 100'
            }
            return true
          },
        },
        componentProps: {
          maskOptions: { mask: Number, min: 0, max: 100, scale: 0, signed: false },
          placeholder: 'Процент для прохождения',
        },
      },
      numberField({
        name: 'questionsCount',
        label: 'Количество вопросов для вывода',
        isRequired: true,
        placeholder: 'Количество вопросов для вывода',
        componentProps: {
          maskOptions: { min: 1, scale: 0 },
        },
      }),
      numberField({
        name: 'hoursTime',
        label: 'Количество часов',
        isRequired: true,
        placeholder: 'Количество часов',
        componentProps: {
          maskOptions: { scale: 0 },
        },
      }),
    ],
  },
]

const getDefaultFormValues = (finalTest?: CourseThemeDetailed | null): FormValues => ({
  passPercentage:
    finalTest?.passPercentage !== null && finalTest?.passPercentage !== undefined
      ? String(finalTest.passPercentage)
      : undefined,
  questionsCount:
    finalTest?.questionsCount !== null && finalTest?.questionsCount !== undefined
      ? String(finalTest.questionsCount)
      : undefined,
  hoursTime: themeMinutesToFormHours(finalTest?.hoursTime || 0),
})

const getDefaultQuestions = (finalTest?: CourseThemeDetailed | null): FinalTestQuestionInput[] => {
  if (finalTest?.questions?.length) {
    return finalTest.questions
  }

  const rawQuestions = (finalTest as unknown as { controlQuestion?: unknown })?.controlQuestion
  if (!rawQuestions) {
    return []
  }

  if (Array.isArray(rawQuestions)) {
    return rawQuestions
      .map((question) => {
        const casted = question as {
          question?: string
          type?: 'single' | 'multi'
          answers?: { text: string; isCorrect: boolean }[]
        }

        if (!casted?.question || !casted?.answers?.length) {
          return null
        }

        return {
          question: casted.question,
          type: casted.type || 'single',
          answers: casted.answers,
        }
      })
      .filter((item): item is FinalTestQuestionInput => item !== null)
  }

  const controlQuestion = rawQuestions as {
    question?: string
    options?: { text: string; isCorrect: boolean }[]
  }

  if (!controlQuestion?.question || !controlQuestion?.options?.length) return []

  return [
    {
      question: controlQuestion.question,
      type: 'single',
      answers: controlQuestion.options,
    },
  ]
}

const ManualFinalTestModal: FC<Props> = ({
  courseId,
  handleCloseModal,
  finalTest,
  isEdit,
  onSuccessVisibleChange,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<
    FetchBaseQueryError | SerializedError | undefined
  >(undefined)
  const [questionsError, setQuestionsError] = useState<string | null>(null)
  const [questions, setQuestions] = useState<FinalTestQuestionInput[]>(() =>
    getDefaultQuestions(finalTest)
  )
  const [questionModalVisible, setQuestionModalVisible] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  // Чем была отправка — созданием или редактированием. Экран успеха должен описывать
  // то, что уже произошло, а проп isEdit для этого не годится: он считается из курса,
  // и после успешного создания курс перезапрашивается, в нём появляется finalTest,
  // isEdit становится true и подменяет заголовок «добавлено» на «изменено» прямо под
  // открытым экраном успеха. null — отправки ещё не было, берём проп как есть.
  const [submittedAsEdit, setSubmittedAsEdit] = useState<boolean | null>(null)

  const [postFinalTestCourseTheme] = usePostFinalTestCourseThemeMutation()
  const [putFinalTestCourseTheme] = usePutFinalTestCourseThemeMutation()
  const [patchCourseFinalTest, patchData] = usePatchCourseFinalTestMutation()

  const methods = useFormProcessor({
    apiErrors: submitError,
    defaultValues: getDefaultFormValues(finalTest),
  })

  useEffect(() => {
    methods.reset(getDefaultFormValues(finalTest))
    setQuestions(getDefaultQuestions(finalTest))
  }, [finalTest, methods])

  const handleSubmitForm = useCallback(
    async (form: FormValues) => {
      if (questions.length === 0) {
        setQuestionsError('Добавьте хотя бы один вопрос')
        return
      }

      setIsSubmitting(true)
      setSubmitError(undefined)
      setQuestionsError(null)

      try {
        const payload = {
          title: FINAL_TEST_TITLE,
          hoursTime: parseFormHoursToMinutes(form.hoursTime),
          test: true,
          source: finalTest?.source || 'manual',
          passPercentage: form.passPercentage ? Number(form.passPercentage) : undefined,
          questionsCount: form.questionsCount ? Number(form.questionsCount) : undefined,
          questions,
        }

        setSubmittedAsEdit(Boolean(isEdit && finalTest?.id))

        if (isEdit && finalTest?.id) {
          await putFinalTestCourseTheme({
            ...payload,
            id: finalTest.id,
            courseTheme: finalTest.courseTheme?.['@id'],
          }).unwrap()
          handleCloseModal()
          return
        }

        const chapterTheme = await postFinalTestCourseTheme(payload).unwrap()

        await patchCourseFinalTest({
          id: courseId,
          finalTest: chapterTheme['@id'],
        }).unwrap()
      } catch (error) {
        setSubmitError(error as FetchBaseQueryError | SerializedError)
      } finally {
        setIsSubmitting(false)
      }
    },
    [courseId, finalTest, handleCloseModal, isEdit, patchCourseFinalTest, postFinalTestCourseTheme, putFinalTestCourseTheme, questions]
  )

  const handleOpenAddModal = () => {
    setEditingIndex(null)
    setQuestionModalVisible(true)
  }

  const handleOpenEditModal = (index: number) => {
    setEditingIndex(index)
    setQuestionModalVisible(true)
  }

  const handleCloseQuestionModal = () => {
    setQuestionModalVisible(false)
    setEditingIndex(null)
  }

  const handleSaveQuestion = (question: FinalTestQuestionInput) => {
    if (editingIndex !== null) {
      setQuestions((prev) => prev.map((q, i) => (i === editingIndex ? question : q)))
    } else {
      setQuestions((prev) => [...prev, question])
    }
    setQuestionsError(null)
    handleCloseQuestionModal()
  }

  const handleSaveQuestionAndAddMore = (question: FinalTestQuestionInput) => {
    if (editingIndex !== null) {
      setQuestions((prev) => prev.map((q, i) => (i === editingIndex ? question : q)))
    } else {
      setQuestions((prev) => [...prev, question])
    }
    setQuestionsError(null)
    setEditingIndex(null)
  }

  const handleDeleteQuestion = (index: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index))
  }

  const ManualFinalTestFooter: FC<FooterProps> = ({
    onSubmitForm,
    isLoading,
    fromButton,
    setFromButton,
    saveText,
  }) => {
    const { handleSubmit } = useFormContext()

    const handleSubmitForm = (values: FieldValues) => {
      setFromButton(FromButtons.MAIN)
      onSubmitForm(prepareValues(values))
    }

    return (
      <FinalTestFooter>
        <SecondaryButton
          type="button"
          onClick={handleOpenAddModal}
          autoWidth
          id="manual-final-test-add-question"
        >
          <PlusOutlined /> Добавить вопрос
        </SecondaryButton>
        <PrimaryButton
          onClick={handleSubmit(handleSubmitForm)}
          type="submit"
          isLoading={fromButton === FromButtons.MAIN && isLoading}
          autoWidth
          id="modal-save"
        >
          {saveText || 'Сохранить'}
        </PrimaryButton>
      </FinalTestFooter>
    )
  }

  const questionsSection = (
    <QuestionsSection>
      <QuestionsSectionTitle>Вопросы</QuestionsSectionTitle>
      {questions.length === 0 ? (
        <QuestionsEmptyPlaceholder>Вопросы не добавлены</QuestionsEmptyPlaceholder>
      ) : (
        <QuestionsList>
          {questions.map((q, index) => (
            <QuestionRow key={index}>
              <QuestionText title={q.question}>{q.question}</QuestionText>
              <QuestionActions>
                <SecondaryButton
                  autoWidth
                  onClick={() => handleOpenEditModal(index)}
                >
                  Изменить
                </SecondaryButton>
                <DeleteButton
                  buttonStyle={ButtonStyle.SECONDARY}
                  autoWidth
                  deleteDescription="Вы уверены, что хотите удалить вопрос?"
                  onClick={() => handleDeleteQuestion(index)}
                />
              </QuestionActions>
            </QuestionRow>
          ))}
        </QuestionsList>
      )}
      {questionsError && <QuestionsErrorText>{questionsError}</QuestionsErrorText>}
      <Modal
        visible={questionModalVisible}
        onModalClose={handleCloseQuestionModal}
        withoutCloseOnOutsideClick
        width="min(1380px, calc(100vw - 40px))"
        contentStyle={{
          borderRadius: 24,
          maxWidth: 'calc(100vw - 40px)',
          padding: 0,
          boxShadow: '0 16px 32px rgba(29, 78, 137, 0.35)',
        }}
      >
        <FinalTestQuestionFormModal
          isOpen={questionModalVisible}
          initialQuestion={editingIndex !== null ? questions[editingIndex] : undefined}
          onSave={handleSaveQuestion}
          onSaveAndAddMore={handleSaveQuestionAndAddMore}
        />
      </Modal>
    </QuestionsSection>
  )

  return (
    <AddNewItemModal
      {...patchData}
      isLoading={isSubmitting || patchData.isLoading}
      handleCloseModal={handleCloseModal}
      onSubmitForm={handleSubmitForm}
      methods={methods}
      fields={fields}
      isEdit={submittedAsEdit ?? isEdit}
      titlePostfix="итоговое тестирование"
      className="manual-final-test-modal"
      minHeightVh={82}
      successContentAddTitle="Итоговое тестирование успешно добавлено"
      successContentEditTitle="Итоговое тестирование успешно изменено"
      // Итоговый тест у курса один, поэтому «Добавить еще» на экране успеха не нужно:
      // кнопка вернула бы к форме с теми же вопросами, и повторное сохранение создало
      // бы второй тест, перепривязав курс на него. Раньше её прятало перещёлкивание
      // isEdit после создания — то самое, которое здесь и чинится.
      withoutAddMore
      CustomFooter={ManualFinalTestFooter}
      onSuccessVisibleChange={onSuccessVisibleChange}
      contentBeforeFields={
        <InstructionList>
          <li>Вы можете добавить любое количество вопросов.</li>
          <li>Сотрудник получит набор вопросов в случайном порядке в указанном вами количестве.</li>
          <li>Ответы будут выводиться в случайном порядке.</li>
          <li>Тестирование считается пройденным, если сотрудник набрал указанный вами процент или выше.</li>
        </InstructionList>
      }
      contentAfterFields={questionsSection}
    />
  )
}

export default ManualFinalTestModal
